(() => {
  const SELECTORS = {
    section: ".timeline-section",
    track: "[data-timeline-track]",
    item: "[data-timeline-item]",
    progress: "[data-timeline-progress]",
    marker: "[data-timeline-marker]",
  };

  function initTimeline(section) {
    if (!window.gsap || !window.ScrollTrigger || !window.Draggable) return;

    const track = section.querySelector(SELECTORS.track);
    const items = section.querySelectorAll(SELECTORS.item);
    const progressBar = section.querySelector(SELECTORS.progress);
    const progressMarker = section.querySelector(SELECTORS.marker);

    if (!track || !items.length) return;

    gsap.registerPlugin(ScrollTrigger, Draggable);

    const mobileQuery = window.matchMedia("(max-width: 989px)");

    let currentX = 0;
    let maxX = 0;
    let tween;
    let trigger;
    let draggableInstance;
    let mobileScrollHandler;

    const getMaxX = () => {
      const viewportWidth = section.clientWidth;
      const fullWidth = track.scrollWidth;
      return Math.max(0, fullWidth - viewportWidth);
    };

    function clampX(value) {
      return Math.max(-maxX, Math.min(0, value));
    }

    function setProgress(progress) {
      const clamped = Math.max(0, Math.min(1, progress));

      if (progressBar) {
        progressBar.style.width = `${clamped * 100}%`;
      }

      if (progressMarker) {
        progressMarker.style.left = `${clamped * 100}%`;

        if (clamped >= 0.999) {
          progressMarker.classList.add("is-end");
        } else {
          progressMarker.classList.remove("is-end");
        }
      }
    }

    function updateHorizontalProgress() {
      const progress = maxX === 0 ? 0 : Math.abs(currentX) / maxX;
      setProgress(progress);
    }

    function updateMobileProgress() {
      const rect = section.getBoundingClientRect();
      const viewportHeight =
        window.innerHeight || document.documentElement.clientHeight;

      const sectionTop = rect.top + window.scrollY;
      const sectionHeight = section.offsetHeight;
      const sectionBottom = sectionTop + sectionHeight;

      const scrollY = window.scrollY;
      const start = sectionTop;
      const end = sectionBottom - viewportHeight;

      let progress;

      if (end <= start) {
        progress = scrollY >= sectionTop ? 1 : 0;
      } else {
        progress = (scrollY - start) / (end - start);
      }

      setProgress(progress);
    }

    function killDesktop() {
      if (tween) {
        tween.kill();
        tween = null;
      }

      if (trigger) {
        trigger.kill();
        trigger = null;
      }

      if (draggableInstance) {
        draggableInstance.kill();
        draggableInstance = null;
      }

      gsap.set(track, { clearProps: "x" });
      currentX = 0;
    }

    function bindMobileProgress() {
      unbindMobileProgress();

      mobileScrollHandler = () => {
        updateMobileProgress();
      };

      window.addEventListener("scroll", mobileScrollHandler, { passive: true });
      window.addEventListener("resize", mobileScrollHandler);
      updateMobileProgress();
    }

    function unbindMobileProgress() {
      if (!mobileScrollHandler) return;

      window.removeEventListener("scroll", mobileScrollHandler);
      window.removeEventListener("resize", mobileScrollHandler);
      mobileScrollHandler = null;
    }

    function animateTo(x, duration = 0.65) {
      const targetX = clampX(x);

      if (tween) tween.kill();

      tween = gsap.to(track, {
        x: targetX,
        duration,
        ease: "power3.out",
        onUpdate: () => {
          currentX = gsap.getProperty(track, "x");
          updateHorizontalProgress();

          if (trigger && maxX > 0) {
            const progress = Math.abs(currentX) / maxX;
            const scrollPosition =
              trigger.start + progress * (trigger.end - trigger.start);
            trigger.scroll(scrollPosition);
          }
        },
      });
    }

    function buildDesktop() {
      unbindMobileProgress();

      maxX = getMaxX();
      currentX = clampX(currentX);

      if (maxX === 0) {
        gsap.set(track, { x: 0 });
        currentX = 0;
        setProgress(0);
        return;
      }

      trigger = ScrollTrigger.create({
        trigger: section,
        start: "top top",
        end: () => `+=${maxX}`,
        pin: true,
        scrub: 1,
        invalidateOnRefresh: true,
        onUpdate: (self) => {
          const nextX = -maxX * self.progress;
          currentX = nextX;
          gsap.set(track, { x: nextX });
          updateHorizontalProgress();
        },
      });

      draggableInstance = Draggable.create(track, {
        type: "x",
        inertia: false,
        allowContextMenu: true,
        cursor: "grab",
        activeCursor: "grabbing",
        bounds: { minX: -maxX, maxX: 0 },
        onPress() {
          if (tween) tween.kill();
        },
        onDrag() {
          currentX = this.x;
          updateHorizontalProgress();

          if (trigger && maxX > 0) {
            const progress = Math.abs(currentX) / maxX;
            const scrollPosition =
              trigger.start + progress * (trigger.end - trigger.start);
            trigger.scroll(scrollPosition);
          }
        },
        onThrowUpdate() {
          currentX = this.x;
          updateHorizontalProgress();
        },
      })[0];

      updateHorizontalProgress();
    }

    function buildMobile() {
      killDesktop();
      bindMobileProgress();
    }

    function build() {
      killDesktop();
      unbindMobileProgress();

      if (mobileQuery.matches) {
        buildMobile();
      } else {
        buildDesktop();
      }
    }

    function wheelHandler(event) {
      if (mobileQuery.matches) return;
      if (maxX <= 0) return;

      event.preventDefault();
      animateTo(currentX - event.deltaY, 0.45);
    }

    section.addEventListener("wheel", wheelHandler, { passive: false });

    const resizeObserver = new ResizeObserver(() => {
      build();
      ScrollTrigger.refresh();
    });

    resizeObserver.observe(section);
    resizeObserver.observe(track);

    build();

    mobileQuery.addEventListener("change", () => {
      build();
      ScrollTrigger.refresh();
    });

    document.addEventListener("shopify:section:load", () => {
      build();
      ScrollTrigger.refresh();
    });

    document.addEventListener("shopify:section:reorder", () => {
      build();
      ScrollTrigger.refresh();
    });

    document.addEventListener("shopify:block:select", () => {
      setTimeout(() => {
        build();
        ScrollTrigger.refresh();
      }, 50);
    });
  }

  function initAll() {
    document.querySelectorAll(SELECTORS.section).forEach(initTimeline);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll);
  } else {
    initAll();
  }
})();
